import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { LocalStorageService } from '../services/local-storage.service';
import { TokenRefreshService } from '../services/token-refresh-service';
import { LoginApiResponse, TokenResponse } from 'src/app/api/identity/models';

const EXCLUDE_URL_LIST: string[] = ['/login'];

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private hasHandledSessionExpiry = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private tokenRefreshService: TokenRefreshService,
    private localStorageService: LocalStorageService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (EXCLUDE_URL_LIST.some(url => request.url.includes(url))) {
      this.hasHandledSessionExpiry = false;
      return next.handle(request);
    }

    const authToken = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    const isRefreshTokenRequest = request.url.includes('/refresh-token');

    const authRequest = this.addToken(
      request,
      authToken,
      refreshToken,
      isRefreshTokenRequest
    );

    return next.handle(authRequest).pipe(
      catchError((error: any) => {
        if (error instanceof HttpErrorResponse) {
          if (error.status === 401 && !isRefreshTokenRequest) {
            if (!authToken) {
              this.handleSessionExpiry(error.error?.message);
            }
            return this.handle401Error(request, next);
          } else if (error.status === 400) {
            if (error.error && error.error.message) {
              // this.toastr.error('Bad Request.');
            } else {
              this.toastr.error('Bad Request.');
            }
          } else {
            console.log('default case error');
          }
        }
        return throwError(() => error);
      })
    );
  }

  private addToken(
    request: HttpRequest<any>,
    authToken: any,
    refreshToken: string | null,
    isRefreshTokenRequest: boolean
  ): HttpRequest<any> {
    const headers: any = {
      Authorization: `Bearer ${
        isRefreshTokenRequest ? refreshToken : authToken
      }`,
    };

    return request.clone({
      setHeaders: headers,
    });
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<any> {
    const refreshToken = localStorage.getItem('refreshToken');
    const currentAccessToken = localStorage.getItem('token');

    if (!refreshToken || !currentAccessToken) {
      this.handleSessionExpiry();
      return throwError(
        () => new Error('No refresh or access token available')
      );
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.tokenRefreshService.refreshToken().pipe(
        catchError(error => {
          this.isRefreshing = false;
          // Notify any waiting requests that refresh failed
          this.refreshTokenSubject.error(error);
          // Re-instantiate the subject for future sessions (though we redirect)
          this.refreshTokenSubject = new BehaviorSubject<any>(null);

          if (
            error.status === 401 ||
            error.status === 403 ||
            error.status === 400
          ) {
            this.handleSessionExpiry(error.error?.message);
          }
          return throwError(() => error);
        }),
        switchMap((newTokenResponse: LoginApiResponse) => {
          this.isRefreshing = false;
          this.hasHandledSessionExpiry = false;
          this.refreshTokenSubject.next(newTokenResponse);
          return next.handle(this.addNewToken(request, newTokenResponse));
        })
      );
    } else {
      return this.waitForTokenRefresh().pipe(
        catchError(err => {
          // If the primary refresh failed, all waiting requests get the error here
          return throwError(() => err);
        }),
        switchMap((newTokenResponse: LoginApiResponse) => {
          return next.handle(this.addNewToken(request, newTokenResponse));
        })
      );
    }
  }

  private addNewToken(
    request: HttpRequest<any>,
    newTokenResponse: LoginApiResponse
  ): HttpRequest<any> {
    const tokenResponseArray = newTokenResponse.data;

    if (tokenResponseArray && tokenResponseArray.length > 0) {
      const tokenResponse: TokenResponse | undefined = tokenResponseArray[0];

      if (tokenResponse && tokenResponse.accessToken) {
        localStorage.setItem('token', tokenResponse.accessToken);
        if (tokenResponse.refreshToken) {
          localStorage.setItem('refreshToken', tokenResponse.refreshToken);
        }
        return this.addToken(
          request,
          tokenResponse.accessToken,
          localStorage.getItem('refreshToken'),
          false
        );
      }
    }

    console.error('No valid access token found in the refresh token response');
    this.handleSessionExpiry();
    throw new Error(
      'No valid access token found in the refresh token response'
    );
  }

  private waitForTokenRefresh(): Observable<LoginApiResponse> {
    return this.refreshTokenSubject.asObservable().pipe(
      filter(token => token !== null),
      take(1)
    );
  }

  private handleSessionExpiry(message?: string): void {
    if (this.hasHandledSessionExpiry) {
      return;
    }

    this.hasHandledSessionExpiry = true;
    this.toastr.error(message || 'Access denied');
    this.localStorageService.clear();
    this.router.navigate(['/auth']);
  }
}
